import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Link2 } from 'lucide-react';
import { createCompatibilitySession } from './services/compatibleApi';
import type { CompatibilityAnswer } from './types/compatible';

const COMPATIBILITY_QUESTIONS = [
  {
    id: 'communication',
    text: 'Comment évaluez-vous votre communication avec votre partenaire ?',
    options: [
      { value: 1, label: 'Difficile' },
      { value: 2, label: 'Moyenne' },
      { value: 3, label: 'Bonne' },
      { value: 4, label: 'Excellente' }
    ]
  },
  // Add more questions
];

const CompatiblePage: React.FC = () => {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState<CompatibilityAnswer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleAnswerChange = (questionId: string, value: number) => {
    setAnswers(prev => {
      const existing = prev.find(a => a.questionId === questionId);
      if (existing) {
        return prev.map(a => a.questionId === questionId ? { ...a, value } : a);
      }
      return [...prev, { questionId, value }];
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const sessionId = await createCompatibilitySession(answers);
      setSessionId(sessionId);
    } catch (error) {
      console.error('Error creating session:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const shareUrl = sessionId ? `${window.location.origin}/compatible/respond/${sessionId}` : '';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in">
      <div className="text-center mb-8">
        <Heart className="mx-auto text-red-500 mb-4" size={48} />
        <h1 className="text-3xl font-bold mb-2">Test de Compatibilité</h1>
        <p className="text-slate-600">
          Découvrez votre niveau de compatibilité avec votre partenaire
        </p>
      </div>

      {!sessionId ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          {COMPATIBILITY_QUESTIONS.map(question => (
            <div key={question.id} className="card p-6">
              <h3 className="text-lg font-medium mb-4">{question.text}</h3>
              <div className="grid grid-cols-2 gap-3">
                {question.options.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleAnswerChange(question.id, option.value)}
                    className={`p-3 rounded-lg border transition-all ${
                      answers.find(a => a.questionId === question.id && a.value === option.value)
                        ? 'bg-sky-50 border-sky-500 text-sky-700'
                        : 'border-slate-200 hover:border-sky-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <button
            type="submit"
            disabled={isLoading || answers.length !== COMPATIBILITY_QUESTIONS.length}
            className="btn-primary w-full"
          >
            {isLoading ? 'Création...' : 'Créer le test'}
          </button>
        </form>
      ) : (
        <div className="card p-6 text-center">
          <h2 className="text-xl font-semibold mb-4">Partagez avec votre partenaire</h2>
          <div className="flex items-center gap-2 mb-4">
            <input
              type="text"
              value={shareUrl}
              readOnly
              className="input-field flex-grow"
            />
            <button
              onClick={handleCopyLink}
              className="btn-primary flex items-center gap-2"
            >
              <Link2 size={18} />
              {copied ? 'Copié !' : 'Copier'}
            </button>
          </div>
          <p className="text-sm text-slate-600">
            Envoyez ce lien à votre partenaire pour qu'il/elle puisse répondre au test
          </p>
        </div>
      )}
    </div>
  );
};

export default CompatiblePage;